import type { SetOptional, StrictOmit } from '@frontend/duck-tape';
import type { MakeInputProps } from '@frontend/forms';
import type { WebTestId } from '@frontend/test-utils';
import { mergeAndConcatClassesObject, safeCss } from '@frontend/web-utils';
import type { TextInputProps as MTextInputProps } from '@mantine/core';
import { TextInput as MTextInput } from '@mantine/core';
import { forwardRef } from 'react';
import type { AccessibilityLabelProps } from '../../../../../types/accessibility';
import type { InputLabelProps } from '../../InputLabel';
import { InputLabel } from '../../InputLabel';

export type TextInputProps = AccessibilityLabelProps &
  StrictOmit<MakeInputProps<string, MTextInputProps, 'labelProps'>, 'classNames'> & {
    classNames?: Partial<typeof textInputClassNames>;
    labelProps?: SetOptional<InputLabelProps, 'label'>;
    labelTextProps?: InputLabelProps['textProps'];
    testId?: WebTestId;
  };

export const textInputClassNames = {
  input: safeCss('text-p-md h-[56px] rounded-sm focus-within:border-textPrimary'),
  root: safeCss('gap-y-sm flex flex-col flex-1'),
};

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      accessibilityLabel,
      classNames,
      label,
      labelProps,
      labelTextProps,
      name,
      onChange,
      showOptionalText,
      testId,
      ...props
    },
    ref,
  ) => {
    return (
      <MTextInput
        aria-label={accessibilityLabel || label}
        classNames={mergeAndConcatClassesObject(textInputClassNames, classNames)}
        data-testid={testId}
        label={
          label ? (
            <InputLabel label={label} showOptionalText={showOptionalText} {...labelProps} textProps={labelTextProps} />
          ) : null
        }
        name={name}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        ref={ref}
        {...props}
      />
    );
  },
);
